import type { UnknownRecord } from '@bonsai-components/utility-types';
import {
  ModButton,
  type ModButtonProps,
  ModMenu,
  ModMenuItem
} from '@moderneinc/design-system-components';
import type { DocumentNode } from 'graphql';
import {
  bindMenu,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';
import React, { type JSX } from 'react';
import { DropDownArrowIcon, GraphQLIcon } from '../../icons/icons';
import {
  GraphQLExplorer,
  type GraphQLExplorerOperationVariables
} from './graphql-explorer.component';

export type QueryExample<T = UnknownRecord> = {
  label: string;
  operationDocument: DocumentNode;
  operationVariables: GraphQLExplorerOperationVariables<T>;
  endpointUrl?: string;
  /** Shown as `#` comments above the operation in the editor. */
  instructions?: string;
};

type SeeHowToRunGraphQLProps<T = UnknownRecord> = {
  examples?: QueryExample<T>[];
  endpointUrl?: string;
  fullScreen?: boolean;
  slots?: {
    button?: React.JSX.Element;
  };
  slotProps?: {
    button?: ModButtonProps;
  };
};

export const SeeHowToRunGraphQL = <T extends UnknownRecord>({
  endpointUrl,
  fullScreen = false,
  examples = [],
  slots = {},
  slotProps = {}
}: SeeHowToRunGraphQLProps<T>): JSX.Element => {
  const popupState = usePopupState({ variant: 'popover', popupId: 'graphql' });

  if (examples.length === 0) {
    return null;
  }
  const firstExample = examples.at(0);
  return examples.length === 1 ? (
    <GraphQLExplorer
      key={firstExample.label}
      slots={{
        button: slots?.button ?? undefined
      }}
      slotProps={{
        graphqlExplorer: {
          endpointUrl: endpointUrl,
          operationDocument: firstExample.operationDocument,
          operationVariables: firstExample.operationVariables,
          instructions: firstExample.instructions
        },
        dialog: {
          // Uses dialogNested as this dialog opens from user org dialog
          sx: { zIndex: (theme) => theme.zIndex.dialogNested },
          fullScreen
        },
        button: slotProps?.button || {
          size: 'small',
          variant: 'secondary',
          sx: {
            justifyContent: 'center'
          },
          startIcon: <GraphQLIcon />
        }
      }}
    >
      API example
    </GraphQLExplorer>
  ) : (
    <React.Fragment>
      {slots?.button ? (
        React.cloneElement(slots.button, {
          ...bindTrigger(popupState),
          ...slotProps?.button,
          active: popupState.isOpen,
          sx: [slots.button.props.sx, slotProps?.button?.sx]
        })
      ) : (
        <ModButton
          {...bindTrigger(popupState)}
          variant="secondary"
          size="small"
          startIcon={<GraphQLIcon />}
          endIcon={<DropDownArrowIcon />}
          data-testid="api-examples-button"
          {...slotProps?.button}
          sx={slotProps?.button?.sx}
        >
          API examples
        </ModButton>
      )}
      <ModMenu
        {...bindMenu(popupState)}
        // Each item is a GraphQLExplorer whose dialog lives inside this menu's
        // subtree. onClick dismisses the menu on selection (MUI menus don't
        // auto-close); keepMounted prevents the menu from unmounting the
        // just-opened dialog when it closes.
        keepMounted
        onClick={() => popupState.close()}
        slotProps={{ paper: { sx: { width: 'min-content' } } }}
      >
        {examples.map(
          ({
            label,
            operationDocument,
            operationVariables,
            endpointUrl: exampleEndpointUrl,
            instructions
          }) => (
            <GraphQLExplorer
              key={label}
              slots={{
                button: <ModMenuItem>{label}</ModMenuItem>
              }}
              slotProps={{
                graphqlExplorer: {
                  endpointUrl: exampleEndpointUrl ?? endpointUrl,
                  operationDocument,
                  operationVariables,
                  instructions
                },
                dialog: {
                  // Uses dialogNested as this dialog opens from user org dialog
                  sx: { zIndex: (theme) => theme.zIndex.dialogNested },
                  fullScreen
                }
              }}
            />
          )
        )}
      </ModMenu>
    </React.Fragment>
  );
};
